/*
Pinia store for auth api calls
This store is the central authority for auth token management and user authentication
*/
import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useStorage } from '@vueuse/core'
import * as authApi from '@/v31-auth/sdk.gen'
import type { ResetDoc, UserDoc, Login } from '@/v31-auth/types.gen'
import { client } from '@/v31-auth/client.gen'

import { createLog } from '@/src/lib/logger'

const log = createLog('Auth API Store')

client.setConfig({
  baseUrl: '/auth/api/',
})

// Token storage constants
const TOKEN_KEY = 'currentUserToken'
const USER_ID_KEY = 'currentUserId'
const APP_TYPE_KEY = 'appType'

// Interface for auth response
interface AuthResponse {
  access_token: string
  expires_in: number
}

// Interface for login payload
export interface LoginPayload {
  userId: string
  token: string
}

// Type for verify user function (will be provided by app-specific stores)
export type AuthAppType = 'buyer' | 'vendor'
export type VerifyUserFn<T> = (userId: string) => Promise<T | null>

export const useAuthApiStore = defineStore('authApiStore', () => {
  // State using useStorage for persistent reactive state
  const accessToken = useStorage<string | null>(TOKEN_KEY, null)
  const currentUserId = useStorage<string | null>(USER_ID_KEY, null)
  const appType = useStorage<AuthAppType | null>(APP_TYPE_KEY, null)
  const isLoading = useStorage<boolean>('auth_loading', false)
  const tokenExpiry = useStorage<number>('tokenExpiry', 0)

  // Computed properties
  const isAuthenticated = computed(() => !!accessToken.value)

  // Token and user management
  function setUserCredentials(userId: string, token: string): void {
    accessToken.value = token
    currentUserId.value = userId
  }

  function clearUserCredentials(): void {
    accessToken.value = null
    currentUserId.value = null
  }

  function setAppType(type: AuthAppType): void {
    appType.value = type
  }

  // Auth API methods
  async function login(loginData: Login): Promise<boolean> {
    isLoading.value = true

    const response = await authApi.postLogin({
      body: loginData,
    })

    if (response.data) {
      const authResponse = response.data as {
        access_token: string
        expires_in: number
      }

      tokenExpiry.value = new Date().getTime() + authResponse.expires_in * 1000

      // Use the user_id from the login form
      handleLoginSuccess(loginData.user_id, authResponse.access_token)
      isLoading.value = false
      return true
    }

    isLoading.value = false
    return false
  }

  async function checkTokenExpiry() {
    if (new Date().getTime() > tokenExpiry.value) {
      try {
        // refresh token
        const ret = await authApi.postRefreshToken()

        // Check if the response contains valid data
        const responseData = ret.data as AuthResponse | undefined
        if (
          responseData &&
          responseData.access_token &&
          responseData.expires_in
        ) {
          const { access_token, expires_in } = responseData

          tokenExpiry.value = new Date().getTime() + expires_in * 1000
          accessToken.value = access_token
        } else {
          // Invalid response - clear credentials and let the user reauth
          clearUserCredentials()
        }
      } catch (error) {
        // Refresh token failed - clear credentials and let the user reauth
        clearUserCredentials()
      }
    }
  }

  // Handle successful login
  function handleLoginSuccess(userId: string, token: string) {
    isLoading.value = true

    // Make sure userId is set properly
    if (!userId) {
      log('Cannot complete login: User ID is missing')
      isLoading.value = false
      return
    }

    // Set credentials with non-empty userId
    setUserCredentials(userId, token)
    isLoading.value = false
  }

  async function logout(): Promise<void> {
    isLoading.value = true
    await authApi.postLogout()
    clearUserCredentials()
    isLoading.value = false
  }

  async function refreshToken(): Promise<AuthResponse | null> {
    isLoading.value = true

    try {
      const response = await authApi.postRefreshToken()
      isLoading.value = false

      if (response.data) {
        return response.data as AuthResponse
      }

      return null
    } catch (error) {
      isLoading.value = false
      return null
    }
  }

  async function requestPasswordReset(userData: UserDoc) {
    isLoading.value = true

    await authApi.postPasswordEmailkey({
      body: userData,
    })

    isLoading.value = false
  }

  async function resetPassword(resetData: ResetDoc) {
    isLoading.value = true

    await authApi.postPasswordUpdate({
      body: resetData,
    })

    isLoading.value = false
  }

  // Verify user authentication and load profile
  async function verifyUser<T>(verifyFn: VerifyUserFn<T>): Promise<T | null> {
    if (!currentUserId.value || !accessToken.value) {
      return null
    }

    isLoading.value = true

    const user = await verifyFn(currentUserId.value)
    isLoading.value = false

    return user
  }

  return {
    // State
    accessToken,
    currentUserId,
    appType,
    isAuthenticated,
    isLoading,

    // Actions
    login,
    logout,
    refreshToken,
    requestPasswordReset,
    resetPassword,
    setUserCredentials,
    clearUserCredentials,
    setAppType,
    verifyUser,
    handleLoginSuccess,
    checkTokenExpiry,
  }
})
